// src/Components/Modules/UserSetting/EditSalary.js
import React, { useState, useEffect, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import {
  Form, Button, Message, Loader, Schema, Divider, toaster, SelectPicker, InputGroup, Input
} from "rsuite";
import "rsuite/dist/rsuite.min.css";
import {
  FaUserTie, FaMoneyBillWave, FaCalendarAlt, FaClipboardList, FaDollarSign, FaArrowLeft, FaPencilAlt
} from "react-icons/fa";

const EditSalary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateSalaryInList } = useContext(MotherContext);
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef();

  const [employmentType, setEmploymentType] = useState('permanent');
  const [companyRules, setCompanyRules] = useState({ basic: 50, rent: 40, medical: 10, conv: 5 });
  const [breakdown, setBreakdown] = useState({ houseRent: 0, medical: 0, conveyance: 0, gross: 0 });
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    AxiosAuthInstance.get("add_employee").then(res => {
      const empList = (res.data.employee || []).map(emp => ({
        label: `${emp.employee_name} (${emp.employee_designation || 'N/A'})`,
        value: emp.id,
        ...emp
      }));
      setEmployees(empList);
    });

    AxiosAuthInstance.get("company/profile").then(res => {
      const rules = res.data.company?.salary_rules || {};
      setCompanyRules({
        basic: rules.basic_percent ?? 50,
        rent: rules.house_rent_percent ?? 40,
        medical: rules.medical_percent ?? 10,
        conv: rules.conveyance_percent ?? 5,
      });
    });
  }, []);

  useEffect(() => {
    const fetchRule = async () => {
      try {
        const res = await AxiosAuthInstance.get(`company/employment-rules/type/${employmentType}`);
        if (res.data.status && res.data.rule?.salary_rules) {
          setCompanyRules({
            basic: res.data.rule.salary_rules.basic_percent ?? companyRules.basic,
            rent: res.data.rule.salary_rules.house_rent_percent ?? companyRules.rent,
            medical: res.data.rule.salary_rules.medical_percent ?? companyRules.medical,
            conv: res.data.rule.salary_rules.conveyance_percent ?? companyRules.conv,
          });
        }
      } catch (err) {}
    };
    fetchRule();
  }, [employmentType]);

  useEffect(() => {
    const fetchSalary = async () => {
      setLoading(true);
      try {
        const res = await AxiosAuthInstance.get(`add_salary/${id}`);
        const salary = res.data.salary;
        
        setEmploymentType(salary.employment_type || 'permanent');
        
        const basic = parseFloat(salary.basic_salary) || 0;
        const rent = parseFloat(salary.house_rent) || (basic * 40) / 100;
        const med = parseFloat(salary.medical_allowance) || (basic * 10) / 100;
        const conv = parseFloat(salary.conveyance) || (basic * 5) / 100;
        const gross = parseFloat(salary.gross_salary) || (basic + rent + med + conv);

        setBreakdown({ houseRent: rent, medical: med, conveyance: conv, gross });
        
        setFormValue({
          employee_id: salary.employee_id,
          month: salary.month,
          basic_salary: basic,
          gross_salary: gross,
          house_rent: rent,
          medical_allowance: med,
          conveyance: conv,
        });
      } catch (error) {
        toaster.push(<Message type="error" duration={5000}>Failed to load salary data.</Message>, { placement: "topEnd" });
        navigate("/salary_setting");
      } finally {
        setLoading(false);
      }
    };
    fetchSalary();
  }, [id, navigate]);

  const handleBasicChange = (val) => {
    const basic = parseFloat(val) || 0;
    const rent = (basic * companyRules.rent) / 100;
    const med = (basic * companyRules.medical) / 100;
    const conv = (basic * companyRules.conv) / 100;
    const gross = basic + rent + med + conv;

    setBreakdown({ houseRent: rent, medical: med, conveyance: conv, gross });
    setFormValue(prev => ({
      ...prev,
      basic_salary: basic,
      gross_salary: gross,
      house_rent: rent,
      medical_allowance: med,
      conveyance: conv,
    }));
  };

  const { StringType, NumberType } = Schema.Types;
  const model = Schema.Model({
    employee_id: StringType().isRequired("Employee is required."),
    month: StringType().isRequired("Month is required."),
    basic_salary: NumberType().min(0, "Basic salary cannot be negative.").isRequired("Basic salary is required."),
  });

  const handleSubmit = async () => {
    if (!formRef.current.check()) return;
    setLoading(true);
    try {
      const payload = {
        ...formValue,
        house_rent: breakdown.houseRent,
        medical_allowance: breakdown.medical,
        conveyance: breakdown.conveyance,
        gross_salary: breakdown.gross,
      };
      
      const res = await AxiosAuthInstance.post(`add_salary/${id}`, payload); // POST with _method or use .put
      if (res.data.status === true) {
        toaster.push(<Message type="success" duration={3000}>Salary updated successfully!</Message>, { placement: "topEnd" });
        if (res.data.salary) updateSalaryInList(res.data.salary);
        navigate("/salary_setting");
      } else {
        toaster.push(<Message type="error">{res.data.message || "Update failed"}</Message>, { placement: "topEnd" });
      }
    } catch (err) {
      const serverErrors = err.response?.data?.errors || {};
      const formattedErrors = Object.keys(serverErrors).reduce((acc, key) => {
        acc[key] = serverErrors[key][0];
        return acc;
      }, {});
      setFormError(formattedErrors);
      toaster.push(<Message type="error">{err.response?.data?.message || "Failed to update salary"}</Message>, { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formValue.month) return <div className="p-5 text-center"><Loader size="lg" /> Loading...</div>;

  return (
    <>
      <Breadcrumb title="Edit Salary" />
      <div className="p-3">
        <div className="card shadow-sm border-0 mx-auto" style={{ maxWidth: "800px" }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: "#4351A3", color: "white" }}>
            <h5 className="mb-0 d-flex align-items-center gap-2"><FaPencilAlt /> Edit Salary Record</h5>
            <Button size="sm" appearance="subtle" className="text-white" onClick={() => navigate("/salary_setting")}>
              <FaArrowLeft className="me-1" /> Back to List
            </Button>
          </div>

          <div className="card-body bg-light p-4">
            <Form ref={formRef} fluid formValue={formValue} formError={formError} onChange={setFormValue} onCheck={setFormError} model={model}>
              
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <Form.Group controlId="employee_id">
                    <Form.ControlLabel>Employee *</Form.ControlLabel>
                    <Form.Control
                      name="employee_id"
                      accepter={SelectPicker}
                      data={employees}
                      onChange={(value, item) => {
                        setFormValue(prev => ({ ...prev, employee_id: value }));
                        if (item?.employment_type) setEmploymentType(item.employment_type);
                      }}
                      style={{ width: "100%" }}
                      cleanable={false}
                      disabled
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="month">
                    <Form.ControlLabel>Month *</Form.ControlLabel>
                    <Form.Control name="month" type="month" prefix={<FaCalendarAlt />} disabled />
                  </Form.Group>
                </div>
              </div>

              <Divider />

              <h6 className="text-primary d-flex align-items-center gap-2 mb-3 border-bottom pb-2">
                <FaDollarSign /> Salary Breakdown (Auto-Calculated)
              </h6>
              
              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <Form.Group controlId="basic_salary">
                    <Form.ControlLabel>Basic Salary *</Form.ControlLabel>
                    <Form.Control
                      name="basic_salary"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter basic salary"
                      prefix={<FaDollarSign />}
                      onChange={handleBasicChange}
                    />
                    <Form.HelpText className="text-muted small">
                      Gross salary auto-calculated based on {employmentType} rules.
                    </Form.HelpText>
                    <Form.ErrorMessage name="basic_salary" />
                  </Form.Group>
                </div>
              </div>

              {/* Salary Breakdown Preview */}
              <div className="mt-3 p-3 border rounded bg-info bg-opacity-10">
                <h6 className="text-primary mb-2"><FaClipboardList className="me-2" /> Salary Breakdown Preview</h6>
                <div className="row text-center small fw-medium">
                  <div className="col-3">
                    <div className="text-muted">Basic</div>
                    <div className="h6 text-primary">৳{(formValue.basic_salary || 0).toFixed(2)}</div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted">House Rent ({companyRules.rent}%)</div>
                    <div className="h6 text-info">৳{breakdown.houseRent.toFixed(2)}</div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted">Medical ({companyRules.medical}%)</div>
                    <div className="h6 text-success">৳{breakdown.medical.toFixed(2)}</div>
                  </div>
                  <div className="col-3">
                    <div className="text-muted">Conveyance ({companyRules.conv}%)</div>
                    <div className="h6 text-warning">৳{breakdown.conveyance.toFixed(2)}</div>
                  </div>
                </div>
                <Divider className="my-2" />
                <div className="row text-center">
                  <div className="col-12">
                    <div className="text-muted fw-bold">Gross Salary</div>
                    <div className="h4 text-dark fw-bold">৳{breakdown.gross.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-3 pt-4 border-top" style={{ marginTop: "2rem" }}>
                <Button appearance="primary" color="blue" size="md" onClick={handleSubmit} disabled={loading} style={{ flex: 1 }}>
                  {loading ? <><Loader size="sm" /> Updating...</> : "Update Salary"}
                </Button>
                <Button className="border" appearance="subtle" color="red" size="md" onClick={() => navigate("/salary_setting")} disabled={loading} style={{ flex: 1 }}>
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditSalary;